import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { isHintSuppressed, foreverValue } from '../shared/utils/ios-push-hint';

/** Evento no estándar que Chrome/Edge/Android lanzan cuando la PWA se puede instalar. */
interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export const INSTALL_HINT_STORAGE_KEY = 'sb_install_hint';
/** Días que se aplaza el aviso cuando el usuario toca «Ahora no». */
export const INSTALL_SNOOZE_DAYS = 7;

/**
 * Guarda el evento `beforeinstallprompt` para ofrecer «Instalar SmartBreak» cuando la persona entra a la
 * app. Debe crearse al arrancar (se inyecta en AppComponent): el evento se emite una sola vez y pronto.
 * En iPhone/iPad no existe ese evento; allí aplica el aviso de iOS (ios-install-banner).
 */
@Injectable({ providedIn: 'root' })
export class PwaInstallService {
  private deferred: BeforeInstallPromptEvent | null = null;
  private readonly available = new BehaviorSubject(false);
  /** true cuando el navegador permite instalar y la app aún no está instalada. */
  readonly canInstall$ = this.available.asObservable();

  constructor() {
    if (typeof window === 'undefined') return;
    window.addEventListener('beforeinstallprompt', (e) => {
      e.preventDefault(); // evita la mini barra del navegador: la oferta la hace nuestro aviso
      this.deferred = e as BeforeInstallPromptEvent;
      this.available.next(!this.isInstalled());
    });
    window.addEventListener('appinstalled', () => {
      this.deferred = null;
      this.available.next(false);
    });
  }

  isInstalled(): boolean {
    if (typeof window === 'undefined') return false;
    return (
      (navigator as Navigator & { standalone?: boolean }).standalone === true ||
      (typeof window.matchMedia === 'function' && window.matchMedia('(display-mode: standalone)').matches)
    );
  }

  /** ¿La persona descartó el aviso (para siempre o con aplazamiento vigente)? */
  isSuppressed(): boolean {
    try {
      return isHintSuppressed(localStorage.getItem(INSTALL_HINT_STORAGE_KEY));
    } catch {
      return false;
    }
  }

  snooze(): void {
    this.save(String(Date.now() + INSTALL_SNOOZE_DAYS * 24 * 60 * 60 * 1000));
  }

  never(): void {
    this.save(foreverValue());
  }

  /** Abre el cuadro de instalación del navegador. Devuelve true si la persona aceptó. */
  async install(): Promise<boolean> {
    const evt = this.deferred;
    if (!evt) return false;
    await evt.prompt();
    const { outcome } = await evt.userChoice;
    this.deferred = null; // el evento solo se puede usar una vez
    this.available.next(false);
    return outcome === 'accepted';
  }

  private save(value: string): void {
    try {
      localStorage.setItem(INSTALL_HINT_STORAGE_KEY, value);
    } catch {
      // sin localStorage el aviso volverá en la siguiente visita
    }
  }
}
